import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import { getInitials } from "../../utilities/helpers";
import CloseIcon from "@mui/icons-material/Close";
import { Drawer } from "@mui/material";
import AvatarSmall from "../../shared/helpers/AvatarSmall";
import DrawerHeader from "../../shared/helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../../shared/helpers/DrawerFooterContainerSmall";
import ButtonFilled from "../../shared/helpers/ButtonFilled";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { formatName } from "../../../../../helpers/helpers";
import { getCardColor2 } from "../../../../../helpers/helpers";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import NotesIcon from "@mui/icons-material/Notes";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import HandymanIcon from "@mui/icons-material/Handyman";
import ComputerIcon from "@mui/icons-material/Computer";
import CampaignIcon from "@mui/icons-material/Campaign";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import EmployeeNotLoggedInWarningModal from "./EmployeeNotLoggedInWarningModal";
import { formatPhoneNumber } from "../../../../../helpers/helpers";

export const SPECIAL_ACCESS_LABELS = {
  logbook_access: "Logbook",
  manager_notifications: "Manager Notifications",
  publish_schedule_approver: "Schedule Publisher",
  documents_editor: "Documents Editor",
  maintenance_access: "Maintenance",
  it_access: "IT",
  marketing_access: "Marketing",
  menu_access: "Menu",
};

export const SPECIAL_ACCESS_ICONS = {
  logbook_access: NotesIcon,
  manager_notifications: NotificationsIcon,
  publish_schedule_approver: TaskAltIcon,
  documents_editor: DescriptionIcon,
  maintenance_access: HandymanIcon,
  it_access: ComputerIcon,
  marketing_access: CampaignIcon,
  menu_access: RestaurantMenuIcon,
};

export const SPECIAL_ACCESS_STYLES = {
  logbook_access: {
    bg: "bg-purple-100",
    text: "text-purple-600",
    border: "border-purple-400",
  },
  manager_notifications: {
    bg: "bg-emerald-100",
    text: "text-emerald-600",
    border: "border-emerald-400",
  },
  publish_schedule_approver: {
    bg: "bg-violet-100",
    text: "text-violet-600",
    border: "border-violet-400",
  },
  documents_editor: {
    bg: "bg-sky-100",
    text: "text-sky-600",
    border: "border-sky-400",
  },
  maintenance_access: {
    bg: "bg-amber-100",
    text: "text-amber-600",
    border: "border-amber-400",
  },
  it_access: {
    bg: "bg-blue-100",
    text: "text-blue-600",
    border: "border-blue-400",
  },
  marketing_access: {
    bg: "bg-pink-100",
    text: "text-pink-600",
    border: "border-pink-400",
  },
  menu_access: {
    bg: "bg-orange-100",
    text: "text-orange-600",
    border: "border-orange-400",
  },
};

// POS imports create employees with placeholder addresses like
// <uuid>@getrightwork.com. These are not deliverable inboxes, so surfaces
// that send email (welcome invites here and on the Company Members page)
// must not treat them as a usable email.
export const isAutoGeneratedEmail = (email) =>
  Boolean(email && email.endsWith("@getrightwork.com") && email.length > 30);

export default function EmployeesTableRow(props) {
  const [isEmployeeHidden, setIsEmployeeHidden] = React.useState(
    props.is_hidden
  );

  const emailIsAutoGenerated = isAutoGeneratedEmail(props.email);

  function toggleEmployeeHidden() {
    if (isEmployeeHidden) {
      props.hide_store_team_member(props.id, "false");
      setIsEmployeeHidden(false);
    } else {
      props.hide_store_team_member(props.id, "true");
      setIsEmployeeHidden(true);
    }
  }

  const shiftCardColor = getCardColor2(
    props.primaryRoleId,
    props.roleNames,
    false
  );

  let permissionInfo = {
    label: "",
    style: "",
  };
  if (props.permissionType === "company_admin") {
    permissionInfo = {
      label: "Admin",
      style: "border border-violet-600 bg-violet-50 text-violet-600",
    };
  } else if (props.permissionType === "company_manager") {
    permissionInfo = {
      label: "Manager",
      style: "border border-orange-600 bg-orange-50 text-orange-600",
    };
  } else if (props.permissionType === "company_timeclock") {
    permissionInfo = {
      label: "Time Clock",
      style: "border border-yellow-600 bg-yellow-50 text-yellow-700",
    };
  } else {
    permissionInfo = {
      label: "Employee",
      style: "border border-blue-600 bg-blue-50 text-blue-600",
    };
  }

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleRowClick = () => {
    props.fetchStoreTeamMember(true, props.id);
    props.setShowUserProfile(true);
  };

  return (
    <>
      <tr
        {...props.provided.draggableProps}
        // {...props.provided.dragHandleProps}
        ref={props.provided.innerRef}
        onClick={handleRowClick}
        className={`${props.snapshot.isDragging ? "bg-slate-200" : "bg-white hover:bg-slate-50"} cursor-pointer`}
      >
        <td
          className={`whitespace-nowrap py-1.5 pl-6 text-sm ${
            props.snapshot.isDragging ? "w-[55px]" : ""
          }`}
        >
          {!props.hideDragHandle && (
            <div
              {...props.provided.dragHandleProps}
              onClick={(e) => e.stopPropagation()}
              className="text-slate-500"
            >
              <DragIndicatorIcon />
            </div>
          )}
        </td>
        <td
          className={`whitespace-nowrap px-3 py-1.5 text-sm ${
            props.snapshot.isDragging ? "w-[112px]" : ""
          }`}
        >
          <div>
            <AvatarSmall
              initials={getInitials(props.firstName, props.lastName)}
              color={shiftCardColor}
            />
          </div>
        </td>
        <td
          className={`whitespace-nowrap px-3 py-1.5 text-sm text-slate-500 ${
            props.snapshot.isDragging ? "w-[140px]" : ""
          }`}
        >
          <div className="font-medium text-slate-900 relative">
            {!props.has_logged_in && !emailIsAutoGenerated && (
              <div
                className="absolute -left-8 bottom-0.5 text-orange-600 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen();
                }}
              >
                <Tooltip
                  title={`${props.firstName} has not logged in yet. Click here if you would like to ${props.welcomeSendCount > 0 ? "send the welcome email again" : "send the welcome email"}.`}
                >
                  <WarningAmberIcon style={{ fontSize: "14px" }} />
                </Tooltip>
              </div>
            )}
            {emailIsAutoGenerated && (
              <div
                className="absolute -left-8 bottom-0.5 text-rose-700 cursor-pointer"
                onClick={async (e) => {
                  e.stopPropagation();
                  await props.fetchStoreTeamMember(true, props.id);
                  props.setShowUserProfile(true);
                  props.setModalEdit(true);
                  props.handleOpen();
                }}
              >
                <Tooltip title="Auto-generated email - click to enter profile.">
                  <WarningIcon style={{ fontSize: "14px" }} />
                </Tooltip>
              </div>
            )}
            <div className="flex items-center gap-2">
              {props.onOpenToastLink &&
                props.posProvider &&
                !props.timeEntriesEnabled && (
                <Tooltip
                  title={
                    props.hasToastLink
                      ? "Linked to POS employee"
                      : "No POS employee linked"
                  }
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onOpenToastLink?.(props.person);
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-1"
                    aria-label="Manage POS link"
                  >
                    {props.hasToastLink ? (
                      <LinkIcon fontSize="inherit" className="text-emerald-600" />
                    ) : (
                      <LinkOffIcon fontSize="inherit" className="text-slate-400" />
                    )}
                  </button>
                </Tooltip>
              )}
              <span>{formatName(`${props.firstName}`)}</span>
            </div>
          </div>
        </td>
        <td
          className={`whitespace-nowrap px-3 py-1.5 text-sm text-slate-500 ${
            props.snapshot.isDragging ? "w-[200px]" : ""
          }`}
        >
          <div className="font-medium text-slate-900">
            {formatName(`${props.lastName}`)}
          </div>
        </td>
        <td
          className={`px-3 py-1.5 text-sm text-slate-500 ${
            props.snapshot.isDragging ? "w-[310px]" : ""
          }`}
        >
            <div className="flex flex-col space-y-1">
              <div className="flex items-center space-x-2 text-slate-900">
                <EmailIcon style={{ fontSize: "16px" }} className="text-slate-400" />
                <span>{props.email || "No email"}</span>
              </div>
              {props.phoneNumber && (
                <div className="flex items-center space-x-2 text-slate-600 text-xs">
                  <PhoneIcon style={{ fontSize: "14px" }} className="text-slate-400" />
                  <span>{formatPhoneNumber(props.phoneNumber)}</span>
              </div>
            )}
          </div>
        </td>
        {(props.clockPinEnabled ?? props.timeEntriesEnabled) && (
          <td
            className={`whitespace-nowrap px-3 py-1.5 text-sm text-slate-500 ${
              props.snapshot.isDragging ? "w-[120px]" : ""
            }`}
          >
            <div className="text-slate-900">
              {props.clockPin ? props.clockPin : "None"}
            </div>
          </td>
        )}
        <td className="whitespace-nowrap px-3 py-1.5 text-sm text-slate-500">
          <div className="flex items-center flex-wrap gap-1">
            <div className="w-fit">
              <div
                className={`${permissionInfo.style} px-2 h-6 rounded-md flex items-center justify-center text-xs`}
              >
                <p>{permissionInfo.label}</p>
              </div>
            </div>
            {props.is_minor && (
              <div className="w-fit">
                <div
                  className={`bg-slate-100 border border-slate-400 text-slate-600 px-2 h-6 rounded-md flex items-center justify-center text-xs`}
                >
                  <p>Minor</p>
                </div>
              </div>
            )}
            {props.permissionTypeOverrides?.map((accessKey) => {
              const Icon = SPECIAL_ACCESS_ICONS[accessKey];
              const styles = SPECIAL_ACCESS_STYLES[accessKey];
              const label = SPECIAL_ACCESS_LABELS[accessKey];
              if (!Icon || !styles || !label) return null;
              return (
                <Tooltip key={accessKey} title={label} placement="top" arrow>
                  <div
                    className={`w-6 h-6 rounded border ${styles.border} ${styles.bg} flex items-center justify-center`}
                  >
                    <Icon
                      className={styles.text}
                      style={{ fontSize: "13px" }}
                    />
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </td>
        {/* <td className="whitespace-nowrap px-3 py-1.5 text-sm text-slate-500">
          <div>{props.person.homeStore}</div>
        </td> */}
        {!props.snapshot.isDragging && (
          <td className="relative whitespace-nowrap py-1.5 pl-3 pr-6 text-right text-sm font-medium flex items-center justify-end space-x-5">
            {isEmployeeHidden && (
              <div className="border border-rose-600 bg-rose-50 text-rose-600 px-2 h-6 rounded-md flex items-center justify-center text-xs">
                <p>hidden</p>
              </div>
            )}
            <div
              className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-500"
              onClick={(e) => {
                e.stopPropagation();
                toggleEmployeeHidden();
              }}
            >
              <Tooltip
                title={
                  isEmployeeHidden
                    ? "Unhide employee from scheduling view"
                    : "Hide employee from scheduling view"
                }
              >
                <IconButton sx={{ width: 32, height: 32 }}>
                  {isEmployeeHidden ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </Tooltip>
            </div>
            {/* <div className="bg-primary hover:bg-blue-700 text-white px-5 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer" onClick={toggleDrawer(true)}>Edit</div> */}
          </td>
        )}
      </tr>
      <EmployeeNotLoggedInWarningModal
        open={open}
        handleClose={handleClose}
        firstName={props.firstName}
        lastName={props.lastName}
        homeStore={props.homeStore}
        stm_id={props.id}
        welcomeSendCount={props.welcomeSendCount}
        phoneNumber={props.phoneNumber}
        emailAddress={props.email}
        onWelcomeEmailSent={() => {
          if (typeof props.refreshEmployees === "function") {
            props.refreshEmployees();
          }
          if (typeof props.fetchStoreTeamMember === "function") {
            props.fetchStoreTeamMember(false, props.id);
          }
        }}
      />
    </>
  );
}
